import React, { useState, useEffect, useCallback } from "react";
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
} from "firebase/auth";
import { auth, db } from "../../firebaseConfig";
import { useNavigate, Link } from "react-router-dom";
import { doc, setDoc, collection, getDocs, onSnapshot, deleteDoc } from "firebase/firestore";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Card, Typography, Grid, MenuItem, Select, Box, TableSortLabel, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, CircularProgress, TablePagination, } from "@mui/material";
import { Snackbar, Alert, } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import ConfirmDialog from "../../utils/ConfirmDialog";

const HrDataDashboard = () => {

    const [sortConfig, setSortConfig] = useState({ key: "date", direction: "asc" });
    const [hrRecords, setHrRecords] = useState([]);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTech, setSelectedTech] = useState("");
    const [selectedCompany, setSelectedCompany] = useState("");
    const [selectedStudent, setSelectedStudent] = useState("");
    const [actionType, setActionType] = useState("view");
    const [, setDashboardData] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);


    useEffect(() => {
        fetchHrRecords();
    }, []);

    useEffect(() => {
        const unsubscribeHrRecords = onSnapshot(collection(db, "calls"), (snapshot) => {
            const hrDataList = snapshot.docs.map((doc) => {
                const data = doc.data();
                let formattedDate = "N/A";
                if (data.createdAt?.seconds) {
                    formattedDate = new Date(data.createdAt.seconds * 1000).toLocaleDateString();
                }
                return { id: doc.id, ...data, date: formattedDate };
            });
            setHrRecords(hrDataList);
        });

        return () => {
            unsubscribeHrRecords();
        };
    }, []);

    useEffect(() => {
        localStorage.setItem("hrDataCount", hrRecords.length);
    }, [hrRecords])

    // ✅ Fetch HR Records (All students' HR data)
    const fetchHrRecords = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, "calls"));
            
            const hrDataList = querySnapshot.docs.map((doc) => {
                const data = doc.data();

                // Convert Firestore timestamp to Date
                let formattedDate = "N/A";
                if (data.createdAt && data.createdAt.seconds) {
                    formattedDate = new Date(data.createdAt.seconds * 1000).toLocaleDateString(); // Convert to readable format
                }

                return {
                    id: doc.id,
                    ...data,
                    date: formattedDate, // Store formatted date
                };
            });
            hrDataList.sort((a, b) => (new Date(a.date) - new Date(b.date)));
            setHrRecords(hrDataList);
        } catch (error) {

            console.error("Error fetching HR records:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const sortedHrRecords = [...hrRecords].sort((a, b) => {
        if (sortConfig.key === "date") {
            return sortConfig.direction === "desc"
                ? new Date(a.date) - new Date(b.date)
                : new Date(b.date) - new Date(a.date);
        }
        return sortConfig.direction === "asc"
            ? a[sortConfig.key]?.localeCompare(b[sortConfig.key])
            : b[sortConfig.key]?.localeCompare(a[sortConfig.key]);
    });

    const handleDelete = useCallback((recordId) => {
        setSelectedRecordId(recordId);
        setConfirmOpen(true);
    }, []);

    const fetchFilteredData = async (date, tech, company, student) => {
        try {
            const querySnapshot = await getDocs(collection(db, "calls"));

            if (querySnapshot.empty) {
                console.warn("Firestore query returned no documents.");
                return [];
            }

            const data = querySnapshot.docs.map(doc => {
                const item = doc.data();
                // Convert Firestore timestamp to Date
                let formattedDate = "N/A";
                if (item.createdAt && item.createdAt.seconds) {
                    formattedDate = new Date(item.createdAt.seconds * 1000).toLocaleDateString(); // Convert to readable format
                }


                return {
                    ...item,
                    date: formattedDate // ✅ Add formatted date field
                };
            });

            // Apply filters
            const filteredData = data.filter(item =>
                (!date || new Date(item.createdAt.seconds * 1000).toISOString().split('T')[0] === date) &&
                (!tech || item.technology?.toLowerCase().includes(tech?.toLowerCase())) &&
                (!company || item.companyName?.toLowerCase() === company?.toLowerCase()) &&
                (!student || item.studentName?.toLowerCase().includes(student?.toLowerCase()))
            );

            setHrRecords(filteredData);
            return filteredData;
        } catch (error) {
            console.error("Error fetching data:", error);
            return [];
        }
    };

    const paginatedHrRecords = sortedHrRecords.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const downloadCSV = (data) => {
        if (!data || data.length === 0) {
            console.error("No data available to download.");
            alert("No data found for the selected filters.");
            return;
        }

        // Exclude "userId" and "createdAt" columns
        const excludedColumns = ["userId", "createdAt"];
        const filteredData = data.map(row => {
            const newRow = { ...row }; // Create a shallow copy
            excludedColumns.forEach(col => delete newRow[col]); // Remove unwanted columns
            return newRow;
        });

        // Convert objects to CSV rows
        const csvRows = [];
        const headers = Object.keys(filteredData[0]);
        csvRows.push(headers.join(",")); // Add header row

        filteredData.forEach(row => {
            const values = headers.map(header => {
                const value = row[header] ?? ""; // Handle null/undefined
                return `"${String(value).replace(/"/g, '""')}"`; // Escape double quotes
            });
            csvRows.push(values.join(","));
        });

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");

        // Download CSV file
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "filtered_data.csv");
        document.body.appendChild(link);
        link.click();
    };


    const handleAction = async () => {
        const filteredData = await fetchFilteredData(selectedDate, selectedTech, selectedCompany, selectedStudent);

        if (filteredData?.length === 0) {
            alert("No data found for the selected filters.");
            return;
        }

        if (actionType === "view") {
            setDashboardData(filteredData); // ✅ Update Dashboard
        } else {
            downloadCSV(filteredData); // ✅ Download Data
        }

        setFilterOpen(false);
    };

    const handleOpen = () => setOpen(true);

    const handleConfirmClose = useCallback(() => {
        setConfirmOpen(false);
        setSelectedRecordId(null);
    }, []);

    const confirmDelete = async (id) => {
        try {
            await deleteDoc(doc(db, "calls", selectedRecordId)); // Adjust collection name as needed
            setHrRecords(prev => prev.filter(record => record.id !== selectedRecordId)); // Update local state
            handleConfirmClose();
            setSnackbar({ open: true, message: "HR Data Deleted successfully!", severity: "success" });
        } catch (error) {
            handleConfirmClose();
            setSnackbar({ open: true, message: "Failed to delete HR record!", severity: "error" });
        }
    };

    const customHeaderCellStyle = {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '130px',
        fontSize: '13px'
    }

    const customHRCellStyle = {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '130px',
        fontSize: '12px'
    }

    return (
        <div style={{ padding: "20px" }}>
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <h3 style={{ color: '#9c27b0' }}>HR Data Records (All Students)</h3>
                <Button endIcon={<FilterListIcon />} variant="contained" color="primary" onClick={() => setFilterOpen(true)}>
                    Filter
                </Button>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" mt={5}>
                    <CircularProgress />
                </Box>) :
                (<TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                                <TableCell style={customHeaderCellStyle}>
                                    <TableSortLabel
                                        active={sortConfig.key === "studentName"}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSort("studentName")}
                                    >
                                        <strong>Student Name</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={customHeaderCellStyle}>
                                    <TableSortLabel
                                        active={sortConfig.key === "companyName"}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSort("companyName")}
                                    >
                                        <strong>Company Name</strong>
                                    </TableSortLabel></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>HR Name</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Contact Number</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}>
                                    <TableSortLabel
                                        active={sortConfig.key === "technology"}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSort("technology")}
                                    >
                                        <strong>Technology</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Location</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Email</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Work Mode</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}><strong>Job Type</strong></TableCell>
                                <TableCell style={customHeaderCellStyle}>
                                    <TableSortLabel
                                        active={sortConfig.key === "date"}
                                        direction={sortConfig.direction}
                                        onClick={() => handleSort("date")}
                                    >
                                        <strong>Date</strong>
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedHrRecords.map((record, id) => (
                                <TableRow key={id} sx={{
                                    position: 'relative',
                                    '&:hover': {
                                        backgroundColor: '#f8fafd',
                                        border: '1px solid #1976d2',
                                        boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
                                    },
                                    '&:hover .delete-button': {
                                        opacity: 1,
                                    },
                                }}>
                                    <TableCell style={customHRCellStyle}>{record.studentName}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.companyName}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.hrName}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.contactNumber}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.technology}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.location}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.email}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.workMode}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.jobType}</TableCell>
                                    <TableCell style={customHRCellStyle}>{record.date}</TableCell>
                                    {/* Delete Button */}
                                    <Box
                                        className="delete-button"
                                        sx={{
                                            position: 'absolute',
                                            right: 16,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            opacity: 0,
                                            transition: 'opacity 0.3s',
                                            cursor: 'pointer',
                                            zIndex: 1,
                                        }}
                                        onClick={() => handleDelete(record.id)}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="error"
                                            fontWeight="bold"
                                            sx={{
                                                backgroundColor: '#9c27b0',
                                                padding: '4px 10px',
                                                borderRadius: '4px',
                                                color: '#fff',
                                                boxShadow: 1,
                                                '&:hover': {
                                                    backgroundColor: '#ff6666',
                                                },
                                            }}
                                        >
                                            Delete
                                        </Typography>
                                    </Box>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={sortedHrRecords.length}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </TableContainer>)}

            <Dialog open={filterOpen} onClose={() => setFilterOpen(false)}>
                <DialogTitle>Select Filters</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Date"
                        type="date"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                    />
                    <TextField
                        label="Technology"
                        fullWidth
                        margin="normal"
                        value={selectedTech}
                        onChange={(e) => setSelectedTech(e.target.value)}
                    />
                    <TextField
                        label="Company"
                        fullWidth
                        margin="normal"
                        value={selectedCompany}
                        onChange={(e) => setSelectedCompany(e.target.value)}
                    />
                    <TextField
                        label="Student Name"
                        fullWidth
                        margin="normal"
                        value={selectedStudent}
                        onChange={(e) => setSelectedStudent(e.target.value)}
                    />

                    <FormControl>
                        <FormLabel>Action</FormLabel>
                        <RadioGroup row value={actionType} onChange={(e) => setActionType(e.target.value)}>
                            <FormControlLabel value="view" control={<Radio />} label="View" />
                            <FormControlLabel value="download" control={<Radio />} label="Download" />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAction} variant="contained">
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmOpen}
                title="Confirm Deletion"
                content={`Are you sure you want to delete this HR Data ?`}
                onClose={handleConfirmClose}
                onConfirm={confirmDelete}
            />
        </div>
    )
}

export default HrDataDashboard;